import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Save, Image as ImageIcon, Info } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';

// Colour picker configuration
const COLOR_FIELDS = [
  {
    id: 'primary_color',
    label: 'Primary Colour',
    key: 'primary_color',
    tooltip: 'Buttons, links, active highlights and the top navigation bar accent throughout your app.'
  },
  {
    id: 'background_color',
    label: 'App Background',
    key: 'background_color',
    tooltip: 'The main page background — the area behind your tiles and content sections.'
  },
  {
    id: 'font_color',
    label: 'Body Text',
    key: 'font_color',
    tooltip: 'General text throughout the app: section headings, descriptions, article excerpts and user names.'
  },
  {
    id: 'tile_color',
    label: 'Tile Background',
    key: 'tile_color',
    tooltip: 'The default background colour for all tiles. Individual tiles can override this with their own colour.'
  },
  {
    id: 'tile_font_color',
    label: 'Tile Text',
    key: 'tile_font_color',
    tooltip: 'The default label text colour on all tiles. Individual tiles can override this with their own text colour.'
  },
  {
    id: 'social_background_color',
    label: 'Social Icon BG',
    key: 'social_background_color',
    tooltip: 'Background colour of the circular buttons behind your social media icons (Facebook, Instagram, etc.).'
  },
  {
    id: 'button_color',
    label: 'Button Colour',
    key: 'button_color',
    tooltip: 'Background colour of action buttons such as the Sign In button. Text colour auto-adjusts for readability.'
  }
];

const FONT_OPTIONS = ['Inter', 'Roboto', 'Open Sans', 'Poppins', 'Montserrat', 'Lato'];
const HEADER_FONT_OPTIONS = [...FONT_OPTIONS, 'Georgia', 'Playfair Display'];
const TILE_FONT_SIZES = [
  { value: 'xs', label: 'Extra Small' },
  { value: 'sm', label: 'Small' },
  { value: 'base', label: 'Base' },
  { value: 'lg', label: 'Large' },
  { value: 'xl', label: 'Extra Large' }
];
const HEADER_SIZES = [
  { value: 'sm', label: 'Small' },
  { value: 'base', label: 'Medium' },
  { value: 'lg', label: 'Large' },
  { value: 'xl', label: 'XL' },
  { value: '2xl', label: '2XL' },
  { value: '3xl', label: '3XL' }
];

export function ColoursAndFontsCard({ designForm, setDesignForm }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Colours & Fonts</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Colour Pickers - Compact Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <TooltipProvider delayDuration={200}>
            {COLOR_FIELDS.map(({ id, label, key, tooltip }) => (
              <div key={id} className="flex items-center gap-2">
                <Input
                  id={id}
                  type="color"
                  value={designForm[key]}
                  onChange={(e) => setDesignForm({ ...designForm, [key]: e.target.value })}
                  className="w-10 h-8 p-0.5 cursor-pointer flex-shrink-0"
                />
                <div className="flex items-center gap-1 min-w-0">
                  <Label htmlFor={id} className="text-xs cursor-pointer leading-tight">{label}</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors" aria-label={`About ${label}`}>
                        <Info className="w-3 h-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[200px] text-center">
                      {tooltip}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            ))}
          </TooltipProvider>
        </div>
        
        {/* Font Controls - Two per row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="font-family" className="text-xs">Font Family</Label>
            <Select
              value={designForm.font_family}
              onValueChange={(value) => setDesignForm({ ...designForm, font_family: value })}
            >
              <SelectTrigger id="font-family" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FONT_OPTIONS.map(font => (
                  <SelectItem key={font} value={font}>{font}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="tile-font-size" className="text-xs">Tile Font Size</Label>
            <Select
              value={designForm.tile_font_size}
              onValueChange={(value) => setDesignForm({ ...designForm, tile_font_size: value })}
            >
              <SelectTrigger id="tile-font-size" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TILE_FONT_SIZES.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function HeaderSettingsCard({ designForm, setDesignForm, onHeaderImageUpload, uploadingHeaderImage }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Header</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Header Text Input with Colour */}
        <div className="space-y-2">
          <Label htmlFor="header-text" className="text-xs">Header Text</Label>
          <div className="flex gap-2">
            <Input
              id="header-text"
              value={designForm.header_text}
              onChange={(e) => setDesignForm({ ...designForm, header_text: e.target.value })}
              placeholder="Welcome to Taptile"
              className="flex-1 h-9"
            />
            <Input
              id="header-text-color"
              type="color"
              value={designForm.header_text_color}
              onChange={(e) => setDesignForm({ ...designForm, header_text_color: e.target.value })}
              className="w-12 h-9 p-0.5 cursor-pointer"
              title="Text colour"
            />
            <Input
              id="header-background-color"
              type="color"
              value={designForm.header_background_color || designForm.primary_color || '#2563EB'}
              onChange={(e) => setDesignForm({ ...designForm, header_background_color: e.target.value })}
              className="w-12 h-9 p-0.5 cursor-pointer"
              title="Header background colour"
            />
          </div>
          <p className="text-xs text-muted-foreground">Text colour · Header background colour</p>
        </div>

        {/* Font, Size, Alignment - Three columns */}
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label htmlFor="header-text-font" className="text-xs">Font</Label>
            <Select
              value={designForm.header_text_font}
              onValueChange={(value) => setDesignForm({ ...designForm, header_text_font: value })}
            >
              <SelectTrigger id="header-text-font" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HEADER_FONT_OPTIONS.map(font => (
                  <SelectItem key={font} value={font}>{font === 'Playfair Display' ? 'Playfair' : font}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="header-text-size" className="text-xs">Size</Label>
            <Select
              value={designForm.header_text_size}
              onValueChange={(value) => setDesignForm({ ...designForm, header_text_size: value })}
            >
              <SelectTrigger id="header-text-size" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HEADER_SIZES.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="header-text-align" className="text-xs">Align</Label>
            <Select
              value={designForm.header_text_align}
              onValueChange={(value) => setDesignForm({ ...designForm, header_text_align: value })}
            >
              <SelectTrigger id="header-text-align" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="center">Centre</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Show/Hide Header Text Toggle - Compact */}
        <div className="flex items-center justify-between py-1">
          <Label htmlFor="show-header-text" className="text-xs cursor-pointer">Show Header Text</Label>
          <Switch
            id="show-header-text"
            checked={designForm.show_header_text}
            onCheckedChange={(checked) => setDesignForm({ ...designForm, show_header_text: checked })}
          />
        </div>

        {/* Header Image - Compact two-column layout */}
        <div className="pt-2 border-t">
          <Label className="text-xs font-medium mb-2 block">Header Image</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="header-image-upload" className="cursor-pointer">
                <div className="flex items-center justify-center gap-1 h-9 px-3 border rounded-md hover:bg-accent text-xs">
                  <ImageIcon className="w-3 h-3" />
                  {uploadingHeaderImage ? 'Uploading...' : 'Upload'}
                </div>
              </Label>
              <input
                id="header-image-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onHeaderImageUpload}
                disabled={uploadingHeaderImage}
              />
            </div>
            <Input
              placeholder="Or paste URL"
              value={designForm.header_image_url?.startsWith('data:') ? '' : designForm.header_image_url}
              onChange={(e) => setDesignForm({ ...designForm, header_image_url: e.target.value })}
              className="h-9 text-xs"
            />
          </div>
          {designForm.header_image_url && (
            <div className="mt-2 flex items-center gap-2">
              <img 
                src={designForm.header_image_url} 
                alt="Header preview" 
                className="h-8 object-contain rounded"
              />
              <Button 
                type="button" 
                variant="ghost" 
                size="sm"
                className="text-xs h-7"
                onClick={() => setDesignForm({ ...designForm, header_image_url: '' })}
              >
                Remove
              </Button>
            </div>
          )}
        </div>

        {/* PWA Icon - Compact two-column layout */}
        <div className="pt-2 border-t">
          <Label className="text-xs font-medium mb-2 block">PWA Icon</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="pwa-icon-upload" className="cursor-pointer">
                <div className="flex items-center justify-center gap-1 h-9 px-3 border rounded-md hover:bg-accent text-xs">
                  <ImageIcon className="w-3 h-3" />
                  Upload
                </div>
              </Label>
              <input
                id="pwa-icon-upload"
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  // Block SVG - iOS doesn't support it for PWA icons
                  if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
                    toast.error('SVG files are not supported for PWA icons. Please upload a PNG, JPG, or WebP image.');
                    e.target.value = '';
                    return;
                  }
                  try {
                    // Validate dimensions client-side
                    await new Promise((resolve, reject) => {
                      const img = new Image();
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        img.onload = () => {
                          if (img.width < 180 || img.height < 180) {
                            toast.warning(`Image is ${img.width}x${img.height}px. For best results on iOS, use at least 180x180 pixels.`);
                          }
                          if (img.width !== img.height) {
                            toast.info(`Image is not square (${img.width}x${img.height}). Square images (e.g., 192x192) work best.`);
                          }
                          resolve();
                        };
                        img.onerror = reject;
                        img.src = event.target.result;
                      };
                      reader.onerror = reject;
                      reader.readAsDataURL(file);
                    });
                    const { url } = await api.uploadPwaIcon(file);
                    setDesignForm({ ...designForm, pwa_icon_url: url });
                    toast.success('PWA icon uploaded');
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Failed to upload PWA icon');
                  } finally {
                    e.target.value = '';
                  }
                }}
              />
            </div>
            <Input
              placeholder="Or paste URL (PNG, JPG, WebP)"
              value={designForm.pwa_icon_url?.startsWith('data:') ? '' : designForm.pwa_icon_url}
              onChange={(e) => {
                const url = e.target.value;
                // Warn if URL looks like SVG
                if (url && (url.toLowerCase().endsWith('.svg') || url.includes('image/svg'))) {
                  toast.error('SVG files are not supported for PWA icons on iOS. Please use a PNG, JPG, or WebP image URL.');
                  return;
                }
                setDesignForm({ ...designForm, pwa_icon_url: url });
              }}
              className="h-9 text-xs"
            />
          </div>
          
          {/* iOS Requirements Warning */}
          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
            <p className="font-medium text-amber-800">⚠️ iOS Requirements:</p>
            <ul className="text-amber-700 mt-1 list-disc list-inside space-y-0.5">
              <li>PNG, JPG, or WebP only (no SVG)</li>
              <li>Minimum 180×180 pixels</li>
              <li>Square images recommended</li>
            </ul>
          </div>
          
          {designForm.pwa_icon_url && (
            <div className="mt-2 flex items-center gap-2 p-2 border rounded bg-gray-50">
              <img 
                src={designForm.pwa_icon_url} 
                alt="PWA icon preview" 
                className="h-10 w-10 object-contain rounded border"
                onError={(e) => {
                  e.target.onerror = null;
                  toast.error('Failed to load icon. URL may be invalid or format unsupported.');
                }}
              />
              <div className="flex-1">
                <span className="text-xs font-medium text-green-600">✓ Icon set</span>
              </div>
              <Button 
                type="button" 
                variant="ghost" 
                size="sm"
                className="text-xs h-7 text-red-500 hover:text-red-700"
                onClick={() => setDesignForm({ ...designForm, pwa_icon_url: '' })}
              >
                Remove
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function AccessControlCard({ designForm, setDesignForm }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Access Control & Security</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="require-auth" className="font-medium">Require Login</Label>
            <p className="text-sm text-muted-foreground">
              Users must sign in to view content
            </p>
          </div>
          <Switch
            id="require-auth"
            checked={designForm.require_authentication}
            onCheckedChange={(checked) => setDesignForm({ ...designForm, require_authentication: checked })}
          />
        </div>

        {designForm.require_authentication && (
          <div className="pl-4 border-l-2 border-muted">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="public-tiles" className="font-medium">Show Public Tiles</Label>
                <p className="text-sm text-muted-foreground">
                  Display unrestricted tiles to logged-out users
                </p>
              </div>
              <Switch
                id="public-tiles"
                checked={designForm.public_tiles_enabled}
                onCheckedChange={(checked) => setDesignForm({ ...designForm, public_tiles_enabled: checked })}
              />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="show-inbox" className="font-medium">Show Inbox Button</Label>
            <p className="text-sm text-muted-foreground">
              Display &quot;View Inbox&quot; button on PWA for logged-in users
            </p>
          </div>
          <Switch
            id="show-inbox"
            checked={designForm.show_inbox_button !== false}
            onCheckedChange={(checked) => setDesignForm({ ...designForm, show_inbox_button: checked })}
          />
        </div>

        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>Current Setting:</strong> {
              !designForm.require_authentication 
                ? '🌐 Public portal - anyone with the link can access' 
                : designForm.public_tiles_enabled
                  ? '🔓 Login required - unrestricted tiles shown as preview'
                  : '🔒 Login required - no content visible until sign in (most secure)'
            }
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export function SaveButton({ onSave, saving }) {
  return (
    <Button 
      type="submit" 
      className="w-full sm:w-auto"
      disabled={saving}
      onClick={onSave}
    >
      <Save className="w-4 h-4 mr-2" />
      {saving ? 'Saving...' : 'Save All Changes'}
    </Button>
  );
}
